import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import OrderInterface from "../interfaces/OrderInterface";
import functionsUser from "../services/user.service";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import moment from "moment";
import statusProps from "../utils/StatusUtils";
import vehicleCategoriesProps from "../utils/VehicleCategoriesUtils";
import UserMessageInterface from "../interfaces/UserMessageInterface";
import MessageIcon from "@mui/icons-material/Message";
import useInterval from "../utils/useInterval";
import { MyTree } from "../components/MyTreeComponent";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Fab,
  Grid,
  Link,
  Modal,
  Paper,
  TextField,
  Typography,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { amber, blue, grey } from "@mui/material/colors";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import NotyfContext from "../contexts/NotyfContext";
import functionsOrder from "../services/order.service";

const ShowOrder: React.FC = () => {
  const { id } = useParams();

  const notyf = useContext(NotyfContext);

  const [order, setOrder] = useState<OrderInterface>();
  const [loadingOrder, setLoadingOrder] = useState<boolean>(true);

  const [modalChatVisible, setModalChatVisible] = useState<boolean>(false);
  const [modalTrackerVisible, setModalTrackerVisible] =
    useState<boolean>(false);
  const [modalCancelOrderVisible, setModalCancelOrderVisible] =
    useState<boolean>(false);
  const [tryingToCancelOrder, setTryingToCancelOrder] =
    useState<boolean>(false);
  const [errorMessageCancelOrder, setErrorMessageCancelOrder] =
    useState<string>("");

  const [newMessage, setNewMessage] = useState<string>("");
  const [messages, setMessages] = useState<UserMessageInterface[]>();
  const [deliveryManLocation, setDeliveryManLocation] = useState<
    {
      userID: string;
      latitude: number;
      longitude: number;
      _id: string;
      coordinate: any;
      id: string;
      date: Date;
    }[]
  >();

  const fetchOrder = (id: string) => {
    functionsUser
      .getOrder(id)
      .then((response) => {
        // console.log(response.data);
        setLoadingOrder(false);
        // console.log(response.data);
        setOrder(response.data);
      })
      .catch((error) => {
        setLoadingOrder(false);
        console.log(error);
      });
  };

  const handleTryToCancelOrder = async () => {
    setTryingToCancelOrder(true);
    setErrorMessageCancelOrder("");
    setTimeout(() => {
      functionsOrder
        .tryToCancelOrder(order?.id ?? "")
        .then((response) => {
          setModalCancelOrderVisible(false);
          notyf.success({
            message: "Pedido cancelado com sucesso",
            position: {
              x: "right",
              y: "top",
            },
          });
          setTryingToCancelOrder(false);
        })
        .catch((error) => {
          setModalCancelOrderVisible(false);
          notyf.error({
            message: "Não foi possível cancelar o pedido",
            position: {
              x: "right",
              y: "top",
            },
          });
          setTryingToCancelOrder(false);
          setErrorMessageCancelOrder("Erro ao cancelar o pedido");
        });
    }, 2000);
  };

  const metadata = useMemo(() => {
    const data = order?.metadata ?? [];

    if (data instanceof Array) {
      return data;
    }

    return [data];
  }, [order]);

  const fetchDeliveryManLocation = useCallback(
    (id: string) => {
      if (
        order?.deliveryManID !== null &&
        order?.deliveryManID !== undefined &&
        order?.deliveryManID !== ""
      ) {
        functionsUser
          .getOrderDeliveryManLocation(order?.deliveryManID)
          .then((response) => {
            // console.log(response.data);
            setDeliveryManLocation(response.data);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    [order?.deliveryManID]
  );

  const fetchMessages = (id: string) => {
    functionsUser
      .getOrderMessages(id)
      .then((response) => {
        setMessages(response.data);
        // console.log(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const sendMessage = (id: string) => {
    functionsUser
      .sendOrderMessage(id, {
        senderID: order?.userID ?? "",
        recipientID: order?.deliveryManID ?? "",
        orderID: id,
        message: newMessage,
        read: false,
        date: new Date(),
        id: "",
      })
      .then((response) => {
        fetchMessages(id);
        setNewMessage("");
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (modalTrackerVisible === true && id !== undefined && id !== "") {
      fetchDeliveryManLocation(id ?? "");
    }
  }, [modalTrackerVisible, id, fetchDeliveryManLocation]);

  useInterval(() => {
    if (modalTrackerVisible === true && id !== undefined && id !== "") {
      fetchDeliveryManLocation(id ?? "");
    }
  }, 5000);

  useEffect(() => {
    if (modalChatVisible === true) {
      fetchMessages(id ?? "");
    }
  }, [modalChatVisible, id]);

  useInterval(() => {
    if (modalChatVisible === true) {
      fetchMessages(id ?? "");
    }
  }, 5000);

  useEffect(() => {
    if (id !== undefined && id !== null) {
      fetchOrder(id);
    }
  }, [id]);

  useInterval(() => {
    if (id !== undefined && id !== null) {
      fetchOrder(id);
    }
  }, 5000);

  return (
    <>
      <Card>
        <CardContent>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={<NavigateNextIcon fontSize="small" />}
          >
            <Link color="inherit" href="/orders">
              Pedidos
            </Link>
            <Typography color="textPrimary">
              {order !== undefined
                ? moment(order?.startTime).format("DD/MM/YYYY - HH:mm")
                : id}
            </Typography>
          </Breadcrumbs>
        </CardContent>
      </Card>
      {loadingOrder ? (
        <>
          <Box mt={2} mb={2} style={{ textAlign: "center" }}>
            <CircularProgress size={70} />
          </Box>
        </>
      ) : (
        <>
          <Container>
            {order ? (
              <>
                <Box mt={2} mb={10}>
                  <Box mt={2} mb={2}>
                    {/* <Button
								variant={"contained"}
								disabled={order.status === "FINISHED" && order.arrivalTime !== undefined && moment(order.arrivalTime).diff(moment(), 'days') > 1}
								color={"secondary"}
								onClick={() => {
									setModalChatVisible(true);
								}}
							>
								Chat com Transportador
							</Button> */}

                    <Button
                      variant={"contained"}
                      disabled={
                        order.status === "FINISHED" &&
                        order.arrivalTime !== undefined &&
                        moment(order.arrivalTime).diff(moment(), "days") > 1
                      }
                      // style={{marginLeft: 16}}
                      color={"primary"}
                      onClick={() => {
                        setModalTrackerVisible(true);
                      }}
                    >
                      Rastrear
                    </Button>

                    <Button
                      variant={"contained"}
                      disabled={order.status !== "SEARCHING_DELIVERY"}
                      // style={{marginLeft: 16}}
                      color={"secondary"}
                      onClick={() => {
                        setModalCancelOrderVisible(true);
                      }}
                      style={{
                        marginLeft: 8,
                      }}
                    >
                      Cancelar pedido
                    </Button>
                  </Box>
                  <Card elevation={2}>
                    <CardHeader
                      title={"Informações gerais"}
                      style={{ backgroundColor: "#DDD" }}
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item lg={4} md={4} xs={6}>
                          <Typography variant={"caption"} component={"p"}>
                            Status
                          </Typography>
                          <Chip
                            variant={"outlined"}
                            size={"small"}
                            label={
                              statusProps.filter(function (status) {
                                return status.status === order.status;
                              })[0].text
                            }
                            color={"primary"}
                            style={{
                              backgroundColor: statusProps.filter(function (
                                status
                              ) {
                                return status.status === order.status;
                              })[0].backgroundColor,
                              color: statusProps.filter(function (status) {
                                return status.status === order.status;
                              })[0].textColor,
                              borderWidth: 0,
                            }}
                          />
                        </Grid>
                        <Grid item lg={4} md={4} xs={6}>
                          <Typography variant={"caption"} component={"p"}>
                            Modal de transporte
                          </Typography>
                          <Chip
                            variant={"outlined"}
                            size={"small"}
                            label={
                              vehicleCategoriesProps.find((category) => {
                                return (
                                  category.number === order.vehicleCategory
                                );
                              })?.label
                            }
                            color={"primary"}
                            style={{
                              backgroundColor: amber[100],
                              color: amber[800],
                              borderWidth: 0,
                            }}
                          />
                        </Grid>
                        <Grid item lg={4} md={4} xs={12}>
                          <Typography variant={"caption"} component={"p"}>
                            Descrição
                          </Typography>
                          <Typography variant={"body1"} component={"p"}>
                            {order.packDescription}
                          </Typography>
                        </Grid>
                        <Grid item lg={4} md={4} xs={6}>
                          <Typography variant={"caption"} component={"p"}>
                            Distância total
                          </Typography>
                          <Typography variant={"body1"} component={"p"}>
                            {order.distance.toFixed(2)} km
                          </Typography>
                        </Grid>
                        <Grid item lg={4} md={4} xs={6}>
                          <Typography variant={"caption"} component={"p"}>
                            Valor
                          </Typography>
                          <Typography variant={"body1"} component={"p"}>
                            {Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(order.price)}
                          </Typography>
                        </Grid>
                        <Grid item lg={4} md={4} xs={12}>
                          <Typography variant={"caption"} component={"p"}>
                            Data de início
                          </Typography>
                          <Typography variant={"body1"} component={"p"}>
                            {moment(order.startTime).format(
                              "DD/MM/YYYY - HH:mm:ss"
                            )}
                          </Typography>
                        </Grid>
                        <Grid item lg={4} md={4} xs={12}>
                          <Typography variant={"caption"} component={"p"}>
                            Transportador
                          </Typography>
                          <Typography variant={"body1"} component={"p"}>
                            {order.deliveryManID !== "" &&
                            order.deliveryManID !== undefined &&
                            order.deliveryMan !== undefined &&
                            order.deliveryMan[0] !== undefined
                              ? order.deliveryMan[0].name
                              : "-/-"}
                          </Typography>
                        </Grid>
                        <Grid item lg={4} md={4} xs={6}>
                          <Typography
                            variant={"caption"}
                            component={"p"}
                            whiteSpace={"nowrap"}
                            overflow={"hidden"}
                            textOverflow={"ellipsis"}
                          >
                            Destinatário
                          </Typography>
                          <Typography
                            variant={"body1"}
                            component={"p"}
                            whiteSpace={"nowrap"}
                            overflow={"hidden"}
                            textOverflow={"ellipsis"}
                          >
                            {order.recipientName ?? "-/-"}
                          </Typography>
                        </Grid>
                        <Grid item lg={4} md={4} xs={6}>
                          <Typography
                            variant={"caption"}
                            component={"p"}
                            whiteSpace={"nowrap"}
                            overflow={"hidden"}
                            textOverflow={"ellipsis"}
                          >
                            Telefone do destinatário
                          </Typography>
                          <Typography variant={"body1"} component={"p"}>
                            {order.recipientPhone ?? "-/-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardHeader
                      title={"Itinerário"}
                      style={{ backgroundColor: "#DDD" }}
                    />
                    <CardContent>
                      <Box
                        sx={{
                          display: { xs: "block", md: "none", lg: "none" },
                        }}
                      >
                        <List dense>
                          <ListItem>
                            <ListItemText
                              primary={`Origem`}
                              secondary={`
													${
                            order.originAddress?.formatted_address +
                            " - " +
                            order.originAddress?.addressLine2
                          }
													${
                            order.originCoordinate.coordinates[1] +
                            ", " +
                            order.originCoordinate.coordinates[0]
                          }
												`}
                            />
                          </ListItem>
                          {order?.waypoints?.map((waypoint, index) => (
                            <ListItemText
                              key={index}
                              primary={`Origem`}
                              secondary={`
													Parada #{index+1}
													${waypoint.address.formatted_address + " " + waypoint.address.addressLine2}
													${
                            waypoint.information !== undefined &&
                            waypoint.information !== ""
                              ? waypoint.information
                              : "Não informado"
                          }
												`}
                            />
                          ))}
                          {/* <ListItem>
                            <ListItemText
                              primary={`Destino`}
                              secondary={`
													${order.toAddress?.formatted_address + " - " + order.toAddress?.addressLine2}
													${
                            order.destinationCoordinate?.coordinates[1] +
                            ", " +
                            order.destinationCoordinate?.coordinates[0]
                          }
												`}
                            />
                          </ListItem> */}
                        </List>
                      </Box>
                      <Box
                        sx={{
                          display: { xs: "none", md: "block", lg: "block" },
                        }}
                      >
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Ordem</TableCell>
                              <TableCell>Endereço</TableCell>
                              <TableCell>Coordenadas</TableCell>
                              <TableCell>Informação extra</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow key={"origin-row"}>
                              <TableCell>Origem</TableCell>
                              <TableCell>
                                {order.originAddress?.formatted_address +
                                  " - " +
                                  order.originAddress?.addressLine2}
                              </TableCell>
                              <TableCell>
                                {order.originCoordinate.coordinates[1] +
                                  ", " +
                                  order.originCoordinate.coordinates[0]}
                              </TableCell>
                              <TableCell>
                                {order.originAddress?.information !==
                                  undefined &&
                                order.originAddress.information !== ""
                                  ? order.originAddress?.information
                                  : "Não informado"}
                              </TableCell>
                            </TableRow>
                            {order?.waypoints?.map((waypoint, index) => (
                              <TableRow key={"waypoint" + index}>
                                <TableCell>Parada #{index + 1}</TableCell>
                                <TableCell>
                                  {waypoint.address.formatted_address +
                                    " " +
                                    waypoint.address.addressLine2}
                                </TableCell>
                                <TableCell>
                                  {waypoint.coordinate?.coordinates[1] +
                                    ", " +
                                    waypoint.coordinate?.coordinates[0]}
                                </TableCell>
                                <TableCell>
                                  {waypoint.information !== undefined &&
                                  waypoint.information !== ""
                                    ? waypoint.information
                                    : "Não informado"}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow key={"destination-row"}>
                              <TableCell>Destino</TableCell>
                              <TableCell>
                                {order.toAddress?.formatted_address +
                                  " - " +
                                  order.toAddress?.addressLine2}
                              </TableCell>
                              <TableCell>
                                {order.destinationCoordinate?.coordinates[1] +
                                  ", " +
                                  order.destinationCoordinate?.coordinates[0]}
                              </TableCell>
                              <TableCell>
                                {order.toAddress?.information !== undefined &&
                                order.toAddress.information !== ""
                                  ? order.toAddress?.information
                                  : "Não informado"}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>

                      <MapContainer
                        center={[
                          order?.originCoordinate?.coordinates[1],
                          order?.originCoordinate?.coordinates[0],
                        ]}
                        zoom={11}
                        style={{ height: "40vh", zIndex: 1 }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker
                          key={"origin-" + order?.id}
                          position={[
                            order?.originCoordinate?.coordinates[1],
                            order?.originCoordinate?.coordinates[0],
                          ]}
                        >
                          <Popup>
                            <Typography variant={"subtitle1"} align={"center"}>
                              Origem
                            </Typography>
                            <Typography variant={"subtitle1"} align={"center"}>
                              {order.originAddress?.formatted_address +
                                " - " +
                                order.originAddress?.addressLine2}
                            </Typography>
                          </Popup>
                        </Marker>
                        {!!order?.destinationCoordinate?.coordinates[0] &&
                          !!order?.destinationCoordinate?.coordinates[1] && (
                            <Marker
                              key={"destination-" + order?.id}
                              position={[
                                order?.destinationCoordinate?.coordinates[1],
                                order?.destinationCoordinate?.coordinates[0],
                              ]}
                            >
                              <Popup>
                                <Typography
                                  variant={"subtitle1"}
                                  align={"center"}
                                >
                                  Destino
                                </Typography>
                                <Typography
                                  variant={"subtitle1"}
                                  align={"center"}
                                >
                                  {order.toAddress?.formatted_address +
                                    " - " +
                                    order.toAddress?.addressLine2}
                                </Typography>
                              </Popup>
                            </Marker>
                          )}

                        {order?.waypoints !== undefined &&
                        order?.waypoints?.length > 0 ? (
                          <>
                            {order.waypoints.map((waypoint, index) => (
                              <Marker
                                key={"waypoint-" + index}
                                position={[
                                  waypoint.coordinate.coordinates[1],
                                  waypoint.coordinate.coordinates[0],
                                ]}
                              >
                                <Popup>
                                  <Typography
                                    variant={"subtitle1"}
                                    align={"center"}
                                  >
                                    Parada #{index + 1}
                                  </Typography>
                                  <Typography
                                    variant={"subtitle1"}
                                    align={"center"}
                                  >
                                    {waypoint.address.formatted_address +
                                      " - " +
                                      waypoint.address.addressLine2}
                                  </Typography>
                                </Popup>
                              </Marker>
                            ))}
                          </>
                        ) : (
                          <></>
                        )}
                      </MapContainer>
                    </CardContent>
                    <CardHeader
                      title={"Acompanhamento"}
                      style={{ backgroundColor: "#DDD" }}
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item lg={4} md={4} xs={12}>
                          <Typography variant={"caption"} component={"p"}>
                            Cadastro
                          </Typography>
                          <Typography variant={"body1"} component={"p"}>
                            {moment(order.startTime).format(
                              "DD/MM/YYYY - HH:mm:ss"
                            )}
                          </Typography>
                        </Grid>
                        <Grid item lg={4} md={4} xs={12}>
                          <Typography variant={"caption"} component={"p"}>
                            Coleta
                          </Typography>
                          <Typography variant={"body1"} component={"p"}>
                            {order.collectedTime !== null &&
                            order.collectedTime !== undefined
                              ? moment(order.startTime).format(
                                  "DD/MM/YYYY - HH:mm:ss"
                                )
                              : "-/-"}
                          </Typography>
                        </Grid>
                        <Grid item lg={4} md={4} xs={12}>
                          <Typography variant={"caption"} component={"p"}>
                            Entrega
                          </Typography>
                          <Typography variant={"body1"} component={"p"}>
                            {order.arrivalTime !== null &&
                            order.arrivalTime !== undefined
                              ? moment(order.arrivalTime).format(
                                  "DD/MM/YYYY - HH:mm:ss"
                                )
                              : "-/-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardHeader
                      title={"Detalhes do pedido"}
                      style={{ backgroundColor: "#DDD" }}
                    />
                    {metadata.length > 0 ? (
                      <CardContent>
                        <MyTree data={metadata} />
                      </CardContent>
                    ) : (
                      <CardContent>Informações indisponíveis.</CardContent>
                    )}
                  </Card>
                </Box>
              </>
            ) : (
              <>
                <Box mt={2}>
                  <Alert severity="error">
                    <AlertTitle>Erro</AlertTitle>
                    Não foi possível encontrar o pedido com o ID —{" "}
                    <strong>{id}</strong>
                  </Alert>
                </Box>
              </>
            )}
          </Container>

          <Modal
            open={modalCancelOrderVisible}
            onClose={() => {
              setModalCancelOrderVisible(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Card>
              <CardHeader title={"Cancelar pedido"} />
              <CardContent>
                {/* <Alert severity="warning">Aviso: O cancelamento de um pedido é uma ação irreversível.</Alert> */}
                <Typography>
                  Você deseja realmente cancelar este pedido?
                </Typography>
                {tryingToCancelOrder ? (
                  <>
                    <Box textAlign={"center"}>
                      <CircularProgress size={60} />
                    </Box>
                  </>
                ) : (
                  <>
                    {errorMessageCancelOrder !== "" ? (
                      <>
                        <Alert severity="error">
                          {errorMessageCancelOrder}
                        </Alert>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </CardContent>
              <CardActions>
                <Button
                  disabled={tryingToCancelOrder}
                  onClick={() => {
                    setModalCancelOrderVisible(false);
                  }}
                >
                  Voltar
                </Button>
                <Button
                  disabled={
                    tryingToCancelOrder ||
                    order?.status !== "SEARCHING_DELIVERY"
                  }
                  onClick={() => {
                    handleTryToCancelOrder();
                  }}
                >
                  Cancelar pedido
                </Button>
              </CardActions>
            </Card>
          </Modal>

          <Modal
            open={modalTrackerVisible}
            onClose={() => {
              setModalTrackerVisible(false);
            }}
            aria-labelledby="simple-modal-tracker-title"
            aria-describedby="simple-modal-tracker-description"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Card
              style={{
                // height: '75vh',
                width: "90vw",
              }}
            >
              <CardHeader
                title={"Rastreamento"}
                subheader={
                  "Atualizado em " +
                  (deliveryManLocation !== undefined
                    ? moment(deliveryManLocation[0].date).format(
                        "DD/MM/YY - HH:mm:ss"
                      )
                    : "Não disponível")
                }
                style={{
                  backgroundColor: "#DDD",
                }}
              />
              <CardContent
                style={{
                  backgroundColor: "#fff",
                  overflow: "scroll",
                  overflowX: "hidden",
                }}
              >
                <MapContainer
                  center={
                    deliveryManLocation !== undefined
                      ? [
                          deliveryManLocation[0]?.latitude ?? 0,
                          deliveryManLocation[0]?.longitude ?? 0,
                        ]
                      : [0, 0]
                  }
                  zoom={2}
                  style={{
                    height: "50vh",
                  }}
                >
                  <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {deliveryManLocation !== undefined &&
                  deliveryManLocation[0] !== null ? (
                    <>
                      <Marker
                        key={order?.id}
                        position={
                          deliveryManLocation !== undefined
                            ? [
                                deliveryManLocation[0]?.latitude ?? 0,
                                deliveryManLocation[0]?.longitude ?? 0,
                              ]
                            : [0, 0]
                        }
                      >
                        <Popup>
                          <Typography variant={"subtitle1"} align={"center"}>
                            Origem
                          </Typography>
                        </Popup>
                      </Marker>
                    </>
                  ) : (
                    <></>
                  )}
                </MapContainer>
              </CardContent>
              <CardActions>
                <Button
                  variant={"contained"}
                  onClick={() => {
                    setModalTrackerVisible(false);
                  }}
                >
                  Fechar
                </Button>
              </CardActions>
            </Card>
          </Modal>

          <Modal
            open={modalChatVisible}
            onClose={() => {
              setModalChatVisible(false);
            }}
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Card
              style={{
                // height: '75vh',
                width: "90vw",
              }}
            >
              <CardHeader
                title={"Chat com o transportador"}
                subheader={"Data de registro"}
                style={{
                  backgroundColor: "#DDD",
                }}
              />
              <CardContent
                style={{
                  backgroundColor: "#fff",
                  height: "50vh",
                  overflow: "scroll",
                  overflowX: "hidden",
                }}
              >
                <Box>
                  {messages !== undefined && messages?.length > 0 ? (
                    <>
                      {messages.map((message: UserMessageInterface) => (
                        <>
                          {message.senderID === order?.userID ? (
                            <>
                              <Box
                                style={{
                                  marginBottom: 16,
                                  textAlign: "right",
                                }}
                              >
                                <Paper
                                  style={{
                                    display: "inline-block",
                                    backgroundColor: blue[900],
                                    color: "#FFF",
                                    maxWidth: "75%",
                                    padding: 8,
                                    paddingLeft: 12,
                                    paddingRight: 12,
                                    borderRadius: 16,
                                  }}
                                >
                                  {message.message}
                                </Paper>
                                <Typography
                                  variant={"caption"}
                                  component={"p"}
                                  style={{ fontSize: 10 }}
                                >
                                  {moment(message.date).format(
                                    "DD/MM - HH:mm:ss"
                                  )}
                                </Typography>
                              </Box>
                            </>
                          ) : (
                            <>
                              <Box
                                style={{
                                  marginBottom: 16,
                                  textAlign: "left",
                                }}
                              >
                                <Paper
                                  style={{
                                    display: "inline-block",
                                    backgroundColor: grey[600],
                                    color: "#FFF",
                                    maxWidth: "75%",
                                    padding: 8,
                                    paddingLeft: 12,
                                    paddingRight: 12,
                                    borderRadius: 16,
                                  }}
                                >
                                  {message.message}
                                </Paper>
                                <Typography
                                  variant={"caption"}
                                  component={"p"}
                                  style={{ fontSize: 10 }}
                                >
                                  {moment(message.date).format(
                                    "DD/MM - HH:mm:ss"
                                  )}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </>
                      ))}
                    </>
                  ) : (
                    <>
                      <Box>
                        <Alert variant={"standard"} severity={"info"}>
                          <AlertTitle>Aviso</AlertTitle>
                          Você ainda não conversou com esse transportador.
                        </Alert>
                      </Box>
                    </>
                  )}
                </Box>
              </CardContent>
              <CardActions
                style={{
                  backgroundColor: "#DDD",
                  flexDirection: "row",
                  padding: 16,
                }}
              >
                <TextField
                  label={"Mensagem"}
                  placeholder={"Digite sua mensagem"}
                  fullWidth
                  variant={"outlined"}
                  style={{
                    backgroundColor: "#FFF",
                  }}
                  value={newMessage}
                  onChange={(event) => {
                    setNewMessage(event.target.value);
                  }}
                  onKeyPress={(ev) => {
                    if (ev.key === "Enter") {
                      // Do code here
                      ev.preventDefault();
                      if (newMessage !== "") {
                        sendMessage(id ?? "");
                      }
                    }
                  }}
                />
                <Fab
                  color="primary"
                  aria-label="add"
                  disabled={newMessage === ""}
                  onClick={() => {
                    sendMessage(id ?? "");
                  }}
                >
                  <MessageIcon />
                </Fab>
              </CardActions>
            </Card>
          </Modal>
        </>
      )}
    </>
  );
};

export default ShowOrder;
